import { AgeCategory, DigibankObjective, Gender } from '@shared-libs/enums';
import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { IDateRange, IStatisticFilter, IStatisticFilters, IStatisticParams } from '@shared-libs/interfaces';

import { ClientOriginProvider } from '@internal-providers/client-origin.provider';
import { DeviceTypeProvider } from '@internal-providers/device-type.provider';
import { DigibankAgencyProvider } from '@internal-providers/digibank-agency.provider';
import { EventAttendanceType } from '@internal-models/event.model';
import { EventTypeProvider } from '@internal-providers/event-type.provider';
import { LocationProvider } from '@internal-providers/location.provider';
import { TargetAudienceProvider } from '@internal-providers/target-audience.provider';
import { isEmpty } from 'lodash';
import { translate } from '@shared-libs/helpers';

@Component({
	selector: 'statistic-filter-component',
	templateUrl: 'statistic-filter.component.html',
	styleUrls: ['./statistic-filter.component.scss'],
})
export class StatisticFilterComponent implements OnInit {
	@Input() public filters: IStatisticFilters = {};
	@Input() public params: IStatisticParams;
	@Input() public paramsChanged: EventEmitter<IStatisticParams> = new EventEmitter();

	public showingFiltersMenu: boolean = false;

	public clientOrigins: Array<{ value: string; name: string }>;
	public deviceTypes: Array<{ value: string; name: string }>;
	public deviceTypeIds: Array<{ value: string; name: string }>;
	public genders: Array<{ value: Gender; name: string }>;
	public ageCategories: Array<{ value: AgeCategory; name: string }>;
	public digibankTargetAudiences: Array<{ value: string; name: string }>;
	public eventTypes: Array<{ value: string; name: string }>;
	public attendanceTypes: Array<{ value: string; name: string }>;
	public agencies: Array<{ value: string; name: string }>;
	public digibankObjectives: Array<{ value: string; name: string }>;
	public locations: Array<{ value: string; name: string }>;

	constructor(
		private readonly targetAudienceProvider: TargetAudienceProvider,
		private readonly clientOriginProvider: ClientOriginProvider,
		private readonly deviceTypeProvider: DeviceTypeProvider,
		private readonly eventTypeProvider: EventTypeProvider,
		private readonly digibankAgencyProvider: DigibankAgencyProvider,
		private readonly locationProvider: LocationProvider
	) {}

	public ngOnInit(): void {
		this.getStaticFilterOptions();
		this.getVariableFilterOptions();
	}

	public filtersAreSet(): boolean {
		return !isEmpty(this.filters);
	}

	public onRefresh(): void {
		this.paramsChanged.emit(this.params);
		this.getVariableFilterOptions();
	}

	public onShowFiltersMenu(): void {
		this.showingFiltersMenu = !this.showingFiltersMenu;
	}

	public onFilterToggle(filter: IStatisticFilter, key: string): void {
		filter.visible = !filter.visible;
		this.onShowFiltersMenu();
		if (!filter.visible && this.params[key]) {
			delete this.params[key];
			this.paramsChanged.emit(this.params);
		}
	}

	public onDateRangeFilter(dateRange: IDateRange): void {
		this.params.dateRange = dateRange;
		this.paramsChanged.emit(this.params);
	}

	public onZipcodeFilter(zipcode: string): void {
		this.params.zipcode = zipcode;
		this.paramsChanged.emit(this.params);
	}

	public onClientOriginFilter(ClientOriginId: string[]): void {
		this.params.ClientOriginId = ClientOriginId;
		this.paramsChanged.emit(this.params);
	}

	public onGenderFilter(gender: Gender[]): void {
		this.params.gender = gender;
		this.paramsChanged.emit(this.params);
	}

	public onAgeCategoryFilter(ageCategory: AgeCategory[]): void {
		this.params.ageCategory = ageCategory;
		this.paramsChanged.emit(this.params);
	}

	public onTargetAudienceFilter(digibankTargetAudience: string[]): void {
		this.params.digibankTargetAudience = digibankTargetAudience;
		this.paramsChanged.emit(this.params);
	}

	public onDevicesFilter(deviceTypes: string[]): void {
		this.params.devices = deviceTypes;
		this.paramsChanged.emit(this.params);
	}

	public onEventTypeFilter(eventTypeIds: string[]): void {
		this.params.EventTypeId = eventTypeIds;
		this.paramsChanged.emit(this.params);
	}

	public onAttendanceTypeFilter(attendanceTypes: EventAttendanceType[]): void {
		this.params.attendanceType = attendanceTypes;
		this.paramsChanged.emit(this.params);
	}

	public onDigibankAgencyFilter(digibankAgencyIds: string[]): void {
		this.params.DigibankAgencyId = digibankAgencyIds;
		this.paramsChanged.emit(this.params);
	}

	public onDigibankObjectiveFilter(digibankObjectives: DigibankObjective[]): void {
		this.params.digibankObjective = digibankObjectives;
		this.paramsChanged.emit(this.params);
	}

	public onLocationFilter(locationIds: string[]): void {
		this.params.LocationId = locationIds;
		this.paramsChanged.emit(this.params);
	}

	public onLocationZipcodeFilter(zipcode: string): void {
		this.params.locationZipcode = zipcode;
		this.paramsChanged.emit(this.params);
	}

	public onDeviceTypeFilter(deviceTypes: string[]): void {
		this.params.DeviceTypeId = deviceTypes;
		this.paramsChanged.emit(this.params);
	}

	private getGenders(): void {
		this.genders = Object.keys(Gender).map((key) => ({
			value: Gender[key],
			name: translate(`CLIENT.GENDER.${key.toUpperCase()}`),
		}));
	}

	private getAgeCategories(): void {
		this.ageCategories = Object.keys(AgeCategory).map((key) => ({
			value: AgeCategory[key],
			name: translate(`CLIENT.AGE_CATEGORY.${AgeCategory[key].toUpperCase().replaceAll('-', '_')}`),
		}));
	}

	private async getDigibankTargetAudiences(): Promise<void> {
		this.digibankTargetAudiences = (await this.targetAudienceProvider.getAll()).map((targetAudience) => ({
			name: targetAudience.name,
			value: targetAudience.name,
		}));
	}

	private async getClientOrigins(): Promise<void> {
		this.clientOrigins = (await this.clientOriginProvider.getAll()).map((clientOrigin) => ({
			name: clientOrigin.name,
			value: clientOrigin.id,
		}));
	}

	private async getDeviceTypes(): Promise<void> {
		this.deviceTypes = (await this.deviceTypeProvider.getAll()).map((deviceType) => ({
			name: deviceType.name,
			value: deviceType.name,
		}));
		this.deviceTypes.push({ name: translate('CLIENT.EXTRA.NO_DEVICE'), value: null });
	}

	private async getDeviceTypeIds(): Promise<void> {
		this.deviceTypeIds = (await this.deviceTypeProvider.getAll()).map((deviceType) => ({
			name: deviceType.name,
			value: deviceType.id,
		}));
	}

	private async getEventTypes(): Promise<void> {
		this.eventTypes = (await this.eventTypeProvider.getAll()).map((eventType) => ({
			name: eventType.name,
			value: eventType.id,
		}));
	}

	private getAttendanceTypes(): void {
		this.attendanceTypes = Object.keys(EventAttendanceType).map((key) => ({
			value: EventAttendanceType[key],
			name: translate(`EVENT_ATTENDANCE_TYPE.${key.toUpperCase()}`),
		}));
	}

	private async getDigibankAgencies(): Promise<void> {
		this.agencies = (await this.digibankAgencyProvider.getAll()).map((digibankAgency) => ({
			name: digibankAgency.name,
			value: digibankAgency.id,
		}));
	}

	private getDigibankObjectives(): void {
		this.digibankObjectives = Object.keys(DigibankObjective)
			.filter((key) => this.filterAllowedValue('digibankObjective', key, DigibankObjective))
			.map((key) => ({
				value: DigibankObjective[key],
				name: translate(`DIGIBANK_OBJECTIVE.${key.toUpperCase()}`),
			}));
	}

	private async getLocations(): Promise<void> {
		this.locations = (await this.locationProvider.getAll()).map((digibankAgency) => ({
			name: digibankAgency.place,
			value: digibankAgency.id,
		}));
		this.locations.push({ name: 'Online', value: null });
	}

	private getStaticFilterOptions() {
		this.getGenders();
		this.getAgeCategories();
		this.getAttendanceTypes();
		this.getDigibankObjectives();
	}

	private getVariableFilterOptions() {
		this.getClientOrigins();
		this.getDeviceTypes();
		this.getDigibankTargetAudiences();
		this.getEventTypes();
		this.getDigibankAgencies();
		this.getLocations();
		this.getDeviceTypeIds();
	}

	private filterAllowedValue(filterName: string, key: string, type: any) {
		if (Array.isArray(this.filters[filterName]?.allowedValues)) {
			return this.filters[filterName].allowedValues.includes(type[key]);
		}
		return true;
	}
}
